import { useEffect, useState } from "react";
import { Save, CreditCard, IndianRupee, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isRoot = user?.role === "ROOT_SUPER_ADMIN";

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <LoadingRows />;

  const pr = { starter_flat: 3000, starter_max: 30, growth_rate: 70, growth_max: 300, unlimited_rate: 50, ...(s.pricing || {}) };
  const setPr = (key, val) => setS({ ...s, pricing: { ...pr, [key]: Number(val) || 0 } });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        grace_days: Number(s.grace_days), trial_days: Number(s.trial_days),
        gateway: s.gateway, maintenance: !!s.maintenance, platform_preset: s.platform_preset || "aurora",
      };
      if (isRoot) payload.pricing = pr;
      await api.put("/admin/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-settings">
      <PageHeader title="Settings" subtitle="Pricing, billing rules and payment gateway configuration.">
        <Button data-testid="settings-save-button" onClick={save} disabled={busy} className="rounded-full font-bold active:scale-95 transition-transform">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Changes"}
        </Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Pricing */}
        <div className="bg-card border border-border rounded-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><IndianRupee className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Partner Pricing — 3-Tier Model</p>
              <p className="text-xs text-muted-foreground">Starter flat · Growth per-site · Unlimited per-site, billed on active websites</p></div>
          </div>
          {!isRoot && (
            <div className="flex items-center gap-3 border border-amber-500/30 bg-amber-500/5 rounded-md p-4 mb-4" data-testid="pricing-root-only-note">
              <Lock className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">Website pricing and platform revenue configuration are controlled exclusively by the Root Super Admin and apply automatically in the background.</p>
            </div>
          )}
          <div className="space-y-4">
            {isRoot && [
              ["starter_flat", "Starter — flat price (₹/month)", "Charged flat for 1 up to Starter-max websites"],
              ["starter_max", "Starter — max websites", "Up to this many active sites pay the flat rate"],
              ["growth_rate", "Growth — ₹ / website / month", "Total active websites × this rate"],
              ["growth_max", "Growth — max websites", "Above this count, the Unlimited rate applies"],
              ["unlimited_rate", "Unlimited — ₹ / website / month", "Total active websites × this rate"],
            ].map(([key, label, hint]) => (
              <div key={key} className="flex items-center gap-3 border border-border rounded-md p-4" data-testid={`pricing-field-${key}`}>
                <div className="flex-1"><p className="text-sm font-bold">{label}</p><p className="text-xs text-muted-foreground">{hint}</p></div>
                <Input data-testid={`pricing-input-${key}`} type="number" value={pr[key]} onChange={(e) => setPr(key, e.target.value)} className="w-28" />
              </div>
            ))}
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="space-y-1.5"><Label className="overline-label">Grace period (days)</Label>
                <Input data-testid="grace-days" type="number" value={s.grace_days} onChange={(e) => setS({ ...s, grace_days: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Trial length (days)</Label>
                <Input data-testid="trial-days" type="number" value={s.trial_days} onChange={(e) => setS({ ...s, trial_days: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-md p-4">
              <div><p className="text-sm font-bold">Maintenance mode</p><p className="text-xs text-muted-foreground">Blocks new website creation platform-wide</p></div>
              <Switch data-testid="maintenance-switch" checked={!!s.maintenance} onCheckedChange={(v) => setS({ ...s, maintenance: v })} />
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Platform color preset (public pages default)</Label>
              <Select value={s.platform_preset || "aurora"} onValueChange={(v) => setS({ ...s, platform_preset: v })}>
                <SelectTrigger data-testid="platform-preset"><SelectValue /></SelectTrigger>
                <SelectContent>{["aurora", "ocean", "sunset", "royal", "neon", "emerald", "violet", "sky", "luxury", "modern"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        </div>

        {/* Gateway */}
        <div className="bg-card border border-border rounded-md p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Payment Gateway</p>
              <p className="text-xs text-muted-foreground">PhonePe — primary gateway</p></div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-2 border border-emerald-500/20 bg-emerald-500/5 rounded-md p-4">
              <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Secret credentials live only in backend environment variables — never exposed to the frontend.
              </p>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Merchant ID</Label>
              <Input data-testid="gateway-merchant" value={s.gateway?.merchant_id || ""} onChange={(e) => setS({ ...s, gateway: { ...s.gateway, merchant_id: e.target.value } })} placeholder="Configured via env in test mode" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="overline-label">Environment</Label>
                <Select value={s.gateway?.env || "uat"} onValueChange={(v) => setS({ ...s, gateway: { ...s.gateway, env: v } })}>
                  <SelectTrigger data-testid="gateway-env"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="uat">UAT / Sandbox</SelectItem><SelectItem value="production">Production</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="overline-label">Mode</Label>
                <Select value={s.gateway?.mode || "test"} onValueChange={(v) => setS({ ...s, gateway: { ...s.gateway, mode: v } })}>
                  <SelectTrigger data-testid="gateway-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="test">Test (Simulated)</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Webhook URL</Label>
              <Input data-testid="gateway-webhook" readOnly value={`${window.location.origin}/api/payments/webhook`} className="font-mono text-xs" /></div>
            <p className="text-xs text-muted-foreground">Architecture is provider-agnostic — Razorpay/Stripe adapters can plug in without changing billing logic.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
